import React, { createContext, useContext, useState, useEffect } from 'react';
import { useApiContext } from './apiContext';
import { useUserContext } from './userContext';

export interface DomainItem {
  id: string;
  name: string;
  description?: string;
  subdomainSlug?: string;
  icon?: string;
  theme?: any;
  portalUrl?: string;
}

export const CORE_DOMAINS: DomainItem[] = [
  {
    id: 'general',
    name: 'General Platform',
    description: 'All-purpose AI assistants, data sources, and workflows',
    icon: 'general',
  },
  {
    id: 'biopharm',
    name: 'BioPharm & Drug Discovery',
    description: 'Multi-omics target discovery, 3D structures, and ADMET screening',
    icon: 'biopharm',
    portalUrl: import.meta.env.VITE_BIOPHARM_APP_URL || (window.location.hostname === 'localhost' ? 'http://localhost:5174' : 'https://www.biopharm.minotii.ai'),
  },
  {
    id: 'education',
    name: 'Education & Learning',
    description: 'Curriculum alignment, research synthesis, and interactive study',
    icon: 'education',
  },
  {
    id: 'itsupport',
    name: 'IT Support & Operations',
    description: 'Incident triage, SLA monitoring, and automated operations',
    icon: 'itsupport',
  },
];

interface IDomainContext {
  activeDomainId: string;
  activeDomain: DomainItem;
  availableDomains: DomainItem[];
  isLoading: boolean;
  switchDomain: (domainId: string) => void;
  launchDomainPortal: (domainId: string) => Promise<void>;
}

const DomainContext = createContext<IDomainContext>({} as IDomainContext);

export const DomainContextProvider: React.FC<{ children: React.ReactNode }> = ({ children }) => {
  const apiContext = useApiContext();
  const userContext = useUserContext();

  const [availableDomains, setAvailableDomains] = useState<DomainItem[]>(CORE_DOMAINS);
  const [activeDomainId, setActiveDomainId] = useState<string>(() => {
    const urlDomain = new URLSearchParams(window.location.search).get('domain');
    if (urlDomain) return urlDomain;
    return localStorage.getItem('activeDomain') || userContext?.user?.domainId || 'general';
  });
  const [isLoading, setIsLoading] = useState<boolean>(false);

  useEffect(() => {
    const handleDomainChange = (e: any) => {
      if (e.detail?.domainId) {
        setActiveDomainId(e.detail.domainId);
      }
    };
    window.addEventListener('minotii-domain-changed', handleDomainChange);
    return () => window.removeEventListener('minotii-domain-changed', handleDomainChange);
  }, []);

  // Sync with available tenant domains from backend
  useEffect(() => {
    const loadDomains = async () => {
      if (!userContext?.user?.tenantId) return;
      setIsLoading(true);
      try {
        const tenantDoms = await apiContext.getTenantDomains();
        if (tenantDoms && Array.isArray(tenantDoms) && tenantDoms.length > 0) {
          const mapped: DomainItem[] = [
            CORE_DOMAINS[0], // always keep general
            ...tenantDoms.map((d: any) => {
              const matchedCore = CORE_DOMAINS.find((c) => c.id === d.id || c.id === d.domainId);
              return {
                id: d.id || d.domainId,
                name: d.name || matchedCore?.name || d.id,
                description: d.description || matchedCore?.description,
                subdomainSlug: d.subdomainSlug || matchedCore?.subdomainSlug,
                icon: d.icon || matchedCore?.icon || 'general',
                theme: d.theme || matchedCore?.theme,
                portalUrl: matchedCore?.portalUrl,
              };
            }),
          ];

          // Deduplicate by ID
          const unique = mapped.filter((v, idx, arr) => arr.findIndex((t) => t.id === v.id) === idx);
          setAvailableDomains(unique);
        }
      } catch (err) {
        // Fallback to core domains on connection error
        setAvailableDomains(CORE_DOMAINS);
      } finally {
        setIsLoading(false);
      }
    };

    loadDomains();
  }, [userContext?.user?.tenantId]);

  const activeDomain = availableDomains.find((d) => d.id === activeDomainId) || CORE_DOMAINS[0];

  const switchDomain = (domainId: string) => {
    localStorage.setItem('activeDomain', domainId);
    setActiveDomainId(domainId);
    window.dispatchEvent(new CustomEvent('minotii-domain-changed', { detail: { domainId } }));

    // Reload the page with the updated domain parameter to cleanly reinitialize all contexts, caches, and services
    const url = new URL(window.location.href);
    url.searchParams.set('domain', domainId);
    if (window.location.href === url.toString()) {
      window.location.reload();
    } else {
      window.location.href = url.toString();
    }
  };

  const launchDomainPortal = async (domainId: string) => {
    const target = availableDomains.find((d) => d.id === domainId);
    let url = target?.portalUrl;
    if (!url && domainId === 'biopharm') {
      url = import.meta.env.VITE_BIOPHARM_APP_URL || (window.location.hostname === 'localhost' ? 'http://localhost:5174' : 'https://www.biopharm.minotii.ai');
    }
    if (url) {
      try {
        const token = await userContext?.getAccessToken?.();
        if (token) {
          url += `${url.includes('?') ? '&' : '?'}token=${encodeURIComponent(token)}`;
        }
      } catch {
        // Continue without token
      }
      window.open(url, '_blank');
    }
  };

  return (
    <DomainContext.Provider
      value={{
        activeDomainId,
        activeDomain,
        availableDomains,
        isLoading,
        switchDomain,
        launchDomainPortal,
      }}
    >
      {children}
    </DomainContext.Provider>
  );
};

export const useDomain = (): IDomainContext => {
  const context = useContext(DomainContext);
  if (!context) {
    throw new Error('useDomain must be used within a DomainContextProvider');
  }
  return context;
};
